// =============================================================================
//  Copyright : 2020 semantics GmbH
//  Date      : $Date: 2025-11-27 17:29:05 +0100 (Thu, 27 Nov 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 158738 $
//  Purpose   : dashboard part for tree/id and general dashboard panels
// =============================================================================
// ---Third-party imports
import React, {createElement} from 'react'

// ---Framework imports
import log from 'uix/logging'
import {uniq, sorted, flatten} from 'ldash'
import {useApi, useMemo} from 'uix/hooks'
import {useTreeNodes} from 'uix/db/hooks'
import {TreeNode} from 'uix/types/components'
import {formatDateTime} from 'uix/utils/format'

// ---Globals
export const TreeParentHistory = ({otid}) => {
  let {data, loading} = useApi('tree/getParentHistory', {otid})
  let otids = useMemo(() => {
    if (!data?.treeParentHistory) return []
    let {treeParentHistory} = data
    let move_ops = [...treeParentHistory.moved, ...treeParentHistory.childmoved]
    return uniq([
      otid,
      ...flatten(move_ops.map(e => [e.parent_old, e.parent_new, e.otid])).filter(Boolean),
    ])
  }, [data])

  let otid2node = useTreeNodes(otids)

  let moved = data?.treeParentHistory.moved || []
  let build_from_to = op => {
    let old_parent_node = '[ROOT]'
    if (op.parent_old) {
      let node_data = otid2node[op.parent_old]
      old_parent_node = node_data ? <TreeNode linkLazy node={node_data} /> : op.parent_old
    }
    let new_parent_node = '[ROOT]'
    if (op.parent_new) {
      let node_data = otid2node[op.parent_new]
      new_parent_node = node_data ? <TreeNode linkLazy node={node_data} /> : op.parent_new
    }
    return (
      <span>
        from {old_parent_node} to {new_parent_node}
      </span>
    )
  }
  let build_ts_user = op => {
    return <span title={`User: ${op.puid}`}>{formatDateTime(op.ts)}: </span>
  }
  let movedEls = sorted(moved, e => e.ts, true).map(op => (
    <li key={`${op.parent_old}_${op.parent_new}`}>
      {build_ts_user(op)} {build_from_to(op)}
    </li>
  ))
  let childmoved = data?.treeParentHistory.childmoved || []
  let childmovedEls = sorted(childmoved, e => e.ts, true).map(op => {
    let moved_node = otid2node[op.otid] ? <TreeNode node={otid2node[op.otid]} /> : op.otid

    return (
      <li key={`${op.otid}_${op.parent_old}_${op.parent_new}`}>
        {build_ts_user(op)} {moved_node} {build_from_to(op)}
      </li>
    )
  })

  let currentNode = otid2node[otid] ? <TreeNode node={otid2node[otid]} /> : null

  let noOps = <div>Keine aufgezeichneten Operationen</div>
  if (loading) return '...'
  return (
    <div>
      <h2>Moved {currentNode} </h2>
      <ul>{movedEls}</ul>
      {movedEls.length == 0 && noOps}
      <h2>Moved Children</h2>
      <ul>{childmovedEls}</ul>
      {childmovedEls.length == 0 && noOps}
    </div>
  )
}
